
import { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { User as UserDataFromZod, UserDataInput } from '@/schemas/user'; // Use User from schemas/user which includes id
import { userSchema } from '@/schemas/user'; // Import Zod schema for User
import { doc, updateDoc, setDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore'; // Import Timestamp
import { db as firestoreInstance } from '@/utils/firebase';
import { z } from 'zod'; // For error handling
import { updateMemberInPortfolios } from "@/utils/firestore/users/portfolioMembership"; // Added import

export const useUserOperations = (currentUserDataFromFirebase: UserDataFromZod | null | undefined) => {
  // Local state for users and currentUser is less critical if FirebaseProvider is the source of truth
  // and components re-render based on FirebaseProvider's updates.
  // These could be removed if SchemaContext consumers always get fresh data from useFirebase().
  const [users, setUsers] = useState<UserDataFromZod[]>([]); 
  const [currentUser, setCurrentUser] = useState<UserDataFromZod | null | undefined>(null);
  
  useEffect(() => {
    setCurrentUser(currentUserDataFromFirebase);
    if (currentUserDataFromFirebase) {
      setUsers([currentUserDataFromFirebase]);
    } else {
      setUsers([]);
    }
  }, [currentUserDataFromFirebase]);
  
// Helper function to convert Date objects in data to Firestore Timestamps
const convertDatesToTimestamps = (data: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const key in data) {
    if (data[key] instanceof Date) {
      result[key] = Timestamp.fromDate(data[key]);
    } else {
      result[key] = data[key];
    }
  }
  return result;
};

  // Wrap operation functions with useCallback for referential stability
  const updateUser = useCallback(async (userId: string, data: Partial<UserDataInput>) => { // Changed type to UserDataInput
    try {
      // Validate the input data (which has Dates from Zod preprocess)
      const validatedData = userSchema.partial().parse(data);
      
      // Convert Date objects to Firestore Timestamps before writing
      const dataForFirestore = convertDatesToTimestamps(validatedData);

      const userRef = doc(firestoreInstance, 'users', userId);
      await updateDoc(userRef, {
        ...dataForFirestore, // Use data with Timestamps
        updatedAt: serverTimestamp(), // This will always be a server timestamp
      });
      console.log(`useUserOperations: Successfully updated user ${userId}`);

      // If this user's data is also denormalized in portfolio members, update there.
      // This logic is moved from userUtils.ts
      if (Object.keys(validatedData).length > 0) { 
        await updateMemberInPortfolios(userId, validatedData);
        console.log(`useUserOperations: Also triggered updateMemberInPortfolios for user ${userId}`);
      }

    } catch (error) {
      console.error(`useUserOperations: Error updating user ${userId} in Firestore (and potentially portfolio memberships):`, error);
      if (error instanceof z.ZodError) {
        // Handle Zod validation errors (e.g., show a toast)
        console.error("Validation errors:", error.errors);
      }
      throw error; // Re-throw to allow caller to handle
    }
  }, [firestoreInstance]); // Added firestoreInstance as dependency, assuming it's stable
  
  // Expects UID to be passed for the document ID.
  // data is the data for the document, excluding fields managed by server/auto or passed as uid.
  // Input data might have Date objects if coming from a form that used Zod parsing.
  const createUser = useCallback(async (uid: string, data: Omit<UserDataInput, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'lastSeen' | 'termsLastVerified' | 'customClaims' | 'status' | 'role' | 'permissions' | 'portfolios' | 'businesses' > & Partial<Pick<UserDataInput, 'status' | 'role' | 'permissions' | 'portfolios' | 'businesses' | 'customClaims' | 'lastSeen' | 'termsLastVerified'>>): Promise<string> => {
    // Convert any Date objects in input 'data' to Timestamps
    const processedInputData = convertDatesToTimestamps(data);

    const documentData = {
      ...processedInputData, // Use processed data
      id: uid, 
      userId: uid, 
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastSeen: serverTimestamp(), // Initialize lastSeen on creation
      // Ensure other optional fields from UserDataInput have defaults if not in 'data'
      status: data.status || 'active',
      role: data.role || 'Manager', // Default role
      permissions: data.permissions || [],
      portfolios: data.portfolios || [],
      businesses: data.businesses || [],
      customClaims: data.customClaims || null,
      termsLastVerified: data.termsLastVerified instanceof Date ? Timestamp.fromDate(data.termsLastVerified) : null,
    };

    try {
      // userSchema expects Date objects after preprocess, but we are constructing for Firestore.
      // For setDoc, it's better to ensure all fields are Firestore-compatible (Timestamps, not Dates).
      // The serverTimestamp() calls handle createdAt, updatedAt, lastSeen.
      // termsLastVerified is handled above.
      // Other fields in documentData are fine.
      // We can parse with userSchema to validate structure, but the output of parse (with Dates)
      // isn't what we directly pass to setDoc if we want Timestamps.
      // Let's ensure documentData aligns with what setDoc expects.
      // The `userSchema.parse` will convert serverTimestamp() placeholders to Date objects due to preprocess.
      // This is not ideal if we want to pass serverTimestamp() directly.
      // For createUser, we construct the object for Firestore directly.
      // We can validate `data` (the input part) against a partial schema if needed.
      
      // Validate the input part of the data
      // Omit only fields that are part of userSchema and are server-set in this function
      const inputSchema = userSchema.partial().omit({ 
        createdAt: true, updatedAt: true, lastSeen: true 
      });
      inputSchema.parse(data); // Validate original input data (with Dates)

      const userRef = doc(firestoreInstance, 'users', uid); 
      // Pass documentData which has serverTimestamp() and correctly formatted Timestamps
      await setDoc(userRef, documentData); 
      console.log(`useUserOperations: Successfully created user document for ${uid}`);
      return uid;
    } catch (error) {
      console.error(`useUserOperations: Error creating user document in Firestore:`, error);
      if (error instanceof z.ZodError) {
        console.error("Validation errors:", error.errors);
      }
      throw error;
    }
  }, [firestoreInstance]); // Added firestoreInstance as dependency
  
  const deleteUser = useCallback(async (userId: string) => {
    try {
      const userRef = doc(firestoreInstance, 'users', userId);
      await deleteDoc(userRef);
      console.log(`useUserOperations: Successfully deleted user ${userId}`);
    } catch (error) {
      console.error(`useUserOperations: Error deleting user ${userId} from Firestore:`, error);
      throw error;
    }
  }, [firestoreInstance]); // Added firestoreInstance as dependency
  
  return {
    users,
    setUsers,
    currentUser,
    setCurrentUser,
    updateUser,
    createUser,
    deleteUser
  };
};
